import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check, X, Type, Loader2 } from 'lucide-react';

const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder = "Select an option...",
    label = "Select",
    icon: Icon = Type,
    className = "w-full sm:w-[240px]",
    loading = false,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Clear search query when dropdown closes (don't pre-fill with current value)
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Common mining/technical synonyms for frontend filtering
    const SYNONYMS = {
        'tyre': ['tire', 'wheel'],
        'tire': ['tyre', 'wheel'],
        'engine': ['motor', 'eng'],
        'electrical': ['electric', 'elec', 'wiring'],
        'hydraulic': ['hyd', 'pump'],
        'transmission': ['trans', 'gearbox'],
        'brake': ['braking', 'retarder'],
        'suspension': ['strut', 'susp', 'suspension cylinder'],
        'suspension cylinder': ['strut', 'susp', 'susp col', 'suspension']
    };

    const getFuzzyMatch = (query, label) => {
        const q = query.toLowerCase();
        const l = label.toLowerCase();

        // 1. Direct match or substring
        if (l.includes(q)) return true;

        // 2. Synonym match
        for (const [key, aliases] of Object.entries(SYNONYMS)) {
            if (q.includes(key) || aliases.some(a => q.includes(a))) {
                if (l.includes(key) || aliases.some(a => l.includes(a))) {
                    return true;
                }
                // Also check if the label matches the key (e.g. "Tire/Wheel" matches "tyre")
                if (l.toLowerCase().includes(key) || aliases.some(a => l.toLowerCase().includes(a))) {
                    return true;
                }
            }
        }

        // 3. Simple fuzzy (Levenshtein-ish or just check if most chars match)
        if (q.length > 4) {
            // Simple check: if query has a typo but shares most characters
            let matches = 0;
            const queryChars = q.split('');
            queryChars.forEach(char => {
                if (l.includes(char)) matches++;
            });
            if (matches / q.length > 0.8) return true;
        }

        return false;
    };

    const filteredOptions = options.filter(option =>
        getFuzzyMatch(searchQuery, option.label)
    );

    const selectedOption = options.find(opt => opt.value === value) || { label: value || '', value: value || '' };

    const handleSelect = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleCustomValue = () => {
        if (searchQuery.trim()) {
            onChange(searchQuery.trim());
            setIsOpen(false);
            setSearchQuery('');
        }
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest absolute -top-2 left-3 bg-white px-1.5 z-10">
                {label}
            </label>

            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => {
                    if (disabled || loading) return;
                    setIsOpen(!isOpen);
                }}
                className={`group w-full flex items-center justify-between bg-white border ${isOpen ? 'border-blue-500 ring-4 ring-blue-500/10' : 'border-gray-200 hover:border-gray-300'} rounded-xl px-4 py-2.5 text-sm transition-all duration-200 shadow-sm ${disabled || loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                aria-disabled={disabled || loading}
            >
                <div className="flex items-center space-x-3 min-w-0 flex-1 mr-2">
                    <div className={`p-1.5 rounded-lg ${isOpen ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100 group-hover:text-gray-500'} transition-colors`}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`block truncate ${selectedOption.value ? 'font-semibold text-gray-700' : 'text-gray-400 font-medium italic'}`}>
                        {loading && !selectedOption.value ? 'Loading options...' : (selectedOption.label || placeholder)}
                    </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-2xl shadow-xl z-50 overflow-hidden animate-scale-up origin-top">
                    {/* Search Box */}
                    <div className="p-3 border-b border-gray-50">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                autoFocus
                                placeholder={placeholder}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (filteredOptions.length === 1) {
                                            handleSelect(filteredOptions[0]);
                                        } else {
                                            handleCustomValue();
                                        }
                                    }
                                }}
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 text-gray-700 font-medium placeholder:text-gray-400"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-[280px] overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                        {searchQuery && !options.some(opt => opt.label.toLowerCase() === searchQuery.toLowerCase()) && (
                            <button
                                onClick={handleCustomValue}
                                className="w-full flex items-center px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 transition-colors font-medium border-b border-gray-50"
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="p-1 rounded bg-blue-100 italic font-bold text-[10px]">USE</div>
                                    <span>"{searchQuery}"</span>
                                </div>
                            </button>
                        )}

                        {loading && filteredOptions.length === 0 ? (
                            <div className="px-4 py-4 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading options...
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleSelect(option)}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors
                    ${option.value === value
                                            ? 'bg-blue-50 text-blue-700 font-bold'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <span className={option.value === value ? 'text-blue-700' : 'text-gray-400 opacity-0'}>
                                            <Check className="w-4 h-4" />
                                        </span>
                                        <span>{option.label}</span>
                                    </div>
                                    {option.count !== undefined && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold
                       ${option.value === value ? 'bg-blue-200/50 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                                            {option.count}
                                        </span>
                                    )}
                                </button>
                            ))
                        ) : searchQuery && (
                            <div className="px-4 py-4 text-center">
                                <p className="text-xs text-gray-400">No matches found. Press Enter to use "{searchQuery}"</p>
                            </div>
                        )}
                    </div>

                    {/* Footer info */}
                    <div className="bg-gray-50/50 px-4 py-2 flex justify-between items-center">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                            {loading ? 'Loading options...' : (searchQuery ? `${filteredOptions.length} matches` : `${options.length} options available`)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
